import { useCallback, useMemo, useRef } from 'react';

import { mcpClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';
import { useAgentPanelStore } from '~/features/chat/hooks/use-agent-panel-store';
import { useAgentPanelStream } from '~/features/chat/hooks/use-agent-panel-stream';
import { agentPanelContextKey } from '~/features/chat/types/agent-panel-context-key';
import type { ChatMessage } from '~/features/chat/types/chat.types';
import { friendlyChatError } from '~/features/chat/utils/friendly-chat-error';
import { useAuthStore } from '~/store/auth.store';
import { nowISO } from '~/utils/date';

import type { ChatbotMessage } from '../types/chatbot-message.types';

let counter = 0;
/** Stable temp id for optimistic messages until the SSE `message_start` swaps in the server id. */
function tempId(): string {
  return `exp-qa-${String(Date.now())}-${String(++counter)}`;
}

interface UseExperimentChatbotQaReturn {
  /**
   * LLM Q&A turns mapped into the chatbot message model — render after the
   * scripted narration. Failed turns map to `assistant-error` entries (one per
   * errored turn) so each keeps its place in the thread with its own retry,
   * rather than collapsing into a single card at the foot of the panel.
   */
  qaMessages: ChatbotMessage[];
  /** True from send until the stream completes/aborts (covers the conversation-create window too). */
  isStreaming: boolean;
  sendMessage: (content: string) => void;
  abortStream: () => void;
  /**
   * Replays a failed turn's prompt into that same bubble (no duplicate user
   * turn). Pass the errored assistant message's id to retry a specific turn;
   * omit it to retry the most recent failure.
   */
  retry: (messageId?: string) => void;
}

/**
 * Live LLM Q&A loop for the Experiment Details chatbot (Phase 5). Talks to the
 * ai-mcp-server ("Lily Analytics MCP Server"): lazily creates the conversation
 * on first send, then streams each turn over SSE with `experiment_id` in the
 * body so the agent scopes its analytics tools to this experiment.
 * Conversation state lives in `useAgentPanelStore` under
 * `experiments:detail:{id}`, isolated from other panels.
 */
export function useExperimentChatbotQa(experimentId: string): UseExperimentChatbotQaReturn {
  const contextKey = agentPanelContextKey.experimentsDetail(experimentId);
  const ctx = useAgentPanelStore((s) => s.byContext[contextKey]);
  const { startStream, abortStream } = useAgentPanelStream(contextKey);
  const lastPromptRef = useRef('');

  // Streams the prompt into an already-prepared placeholder. Creates the
  // server-side conversation first when this context doesn't have one yet
  // (live ai-mcp-server returns `{conversation_id}` snake-case; MSW returns
  // `{id}` — accept either until the backend conforms to lily-copilot.md).
  const streamIntoPlaceholder = useCallback(
    (content: string, placeholderId: string) => {
      const store = useAgentPanelStore.getState();
      const tenantId = useAuthStore.getState().user?.activeTenantId;
      const scope: Record<string, unknown> = { experiment_id: experimentId };
      if (tenantId) scope.tenant_id = tenantId;

      const existingId = store.byContext[contextKey]?.conversationId;
      if (existingId) {
        startStream(existingId, content, scope);
        return;
      }

      void mcpClient
        .post(endpoints.chat.create(), { json: tenantId ? { tenant_id: tenantId } : {} })
        .json<{ id?: string; conversation_id?: string }>()
        .then((conv) => {
          const convId = conv.id ?? conv.conversation_id;
          if (!convId) {
            throw new Error('Conversation create response missing id / conversation_id');
          }
          useAgentPanelStore.setState((s) => {
            const current = s.byContext[contextKey];
            if (!current) return s;
            return {
              byContext: { ...s.byContext, [contextKey]: { ...current, conversationId: convId } },
            };
          });
          // Bail if the user hit Stop while the create round-trip was in flight.
          const current = useAgentPanelStore.getState().byContext[contextKey];
          if (current?.streamingMessageId !== placeholderId) return;
          startStream(convId, content, scope);
        })
        .catch((err: unknown) => {
          const s = useAgentPanelStore.getState();
          s.updateMessage(contextKey, placeholderId, {
            status: 'error',
            content: [
              {
                type: 'error',
                message: friendlyChatError({ raw: err, source: 'conversation-create' }),
                retryable: true,
              },
            ],
          });
          s.setStreaming(contextKey, false);
        });
    },
    [contextKey, experimentId, startStream],
  );

  const sendMessage = useCallback(
    (content: string) => {
      const store = useAgentPanelStore.getState();
      const current = store.getOrInitContext(contextKey);
      if (current.isStreaming) return; // one turn at a time — input shows Stop while streaming

      lastPromptRef.current = content;

      const userMessage: ChatMessage = {
        id: tempId(),
        conversationId: current.conversationId ?? 'pending',
        role: 'user',
        content: [{ type: 'text', text: content }],
        status: 'complete',
        createdAt: nowISO(),
      };
      store.addMessage(contextKey, userMessage);

      const placeholder: ChatMessage = {
        id: tempId(),
        conversationId: current.conversationId ?? 'pending',
        role: 'assistant',
        content: [],
        status: 'streaming',
        createdAt: nowISO(),
      };
      store.addMessage(contextKey, placeholder);
      store.setStreaming(contextKey, true, placeholder.id);

      streamIntoPlaceholder(content, placeholder.id);
    },
    [contextKey, streamIntoPlaceholder],
  );

  // Resets a failed assistant bubble back to streaming and replays its prompt
  // into the same turn — avoids duplicating the user turn a re-send would.
  // `messageId` targets a specific card's turn (per-card retry); omitting it
  // retries the most recent failure. The prompt is the user turn immediately
  // preceding that turn, so retrying an older card replays *its* question.
  const retry = useCallback(
    (messageId?: string) => {
      const store = useAgentPanelStore.getState();
      const ctx = store.byContext[contextKey];
      if (!ctx || ctx.isStreaming) return; // never start a retry over an in-flight turn
      const messages = ctx.messages;
      // A card's turn is either errored or completed-but-empty — both expose
      // Retry — so the messageId path matches by id alone; the no-arg fallback
      // still targets the latest errored turn.
      const target = messageId
        ? messages.find((m) => m.id === messageId && m.role === 'assistant')
        : [...messages].reverse().find((m) => m.role === 'assistant' && m.status === 'error');
      if (!target) {
        if (lastPromptRef.current) sendMessage(lastPromptRef.current);
        return;
      }
      const idx = messages.findIndex((m) => m.id === target.id);
      const preceding = idx > 0 ? messages[idx - 1] : undefined;
      const prompt =
        preceding?.role === 'user' ? blocksToText(preceding.content).trim() : lastPromptRef.current;
      if (!prompt) return;
      lastPromptRef.current = prompt;
      store.updateMessage(contextKey, target.id, { status: 'streaming', content: [] });
      store.setStreaming(contextKey, true, target.id);
      streamIntoPlaceholder(prompt, target.id);
    },
    [contextKey, sendMessage, streamIntoPlaceholder],
  );

  const messages = ctx?.messages;
  const streamingMessageId = ctx?.streamingMessageId ?? null;

  // Every turn — including failed ones — maps to a renderable message, so a
  // failure surfaces an inline `assistant-error` card in its own position
  // instead of collapsing all failures into one card at the foot of the panel.
  const qaMessages = useMemo<ChatbotMessage[]>(
    () => toQaMessages(messages ?? [], streamingMessageId),
    [messages, streamingMessageId],
  );

  return {
    qaMessages,
    isStreaming: ctx?.isStreaming ?? false,
    sendMessage,
    abortStream,
    retry,
  };
}

/** Shown when an errored turn somehow carries no error block (defensive — every FE error path sets one). */
const FALLBACK_QA_ERROR = 'Lily ran into a problem answering that. Please try again.';

/**
 * Maps chat-store messages onto the chatbot's renderable model: user turns →
 * `user` bubbles, assistant turns → `assistant-text` (empty + streaming shows
 * the blinking-cursor "thinking" bubble), failed assistant turns →
 * `assistant-error` so each failure keeps its place in the thread with its own
 * inline retry card.
 */
function toQaMessages(
  messages: ChatMessage[],
  streamingMessageId: string | null,
): ChatbotMessage[] {
  return messages.flatMap<ChatbotMessage>((msg) => {
    if (msg.role === 'user') {
      const text = blocksToText(msg.content).trim();
      return text ? [{ kind: 'user', id: msg.id, text }] : [];
    }
    if (msg.status === 'error') {
      const block = Array.isArray(msg.content)
        ? msg.content.find((b) => b.type === 'error')
        : undefined;
      return [
        {
          kind: 'assistant-error',
          id: msg.id,
          message: block?.type === 'error' ? block.message : FALLBACK_QA_ERROR,
          // FE-synthesized errors omit the flag and are always retryable; honor
          // the server's `retryable: false` to drop the pointless Retry button.
          retryable: block?.type === 'error' ? block.retryable : true,
        },
      ];
    }
    const streaming = msg.status === 'streaming' && msg.id === streamingMessageId;
    const text = blocksToText(msg.content);
    // Completed turn with no renderable text — the agent returned an empty
    // `message_complete` (observed live). Surface a subtle retryable "no
    // response" card rather than a silent blank bubble.
    if (!streaming && text.trim() === '') {
      return [{ kind: 'assistant-empty', id: msg.id }];
    }
    return [{ kind: 'assistant-text', id: msg.id, text, streaming }];
  });
}

/**
 * Flattens content blocks to plain text. Defensive against a non-array
 * `content` (the live server has shipped `content` as a bare string before) so
 * a wire-shape drift degrades to rendering the string rather than crashing the
 * panel with `.map is not a function`.
 */
function blocksToText(content: ChatMessage['content']): string {
  if (typeof content === 'string') return content;
  if (!Array.isArray(content)) return '';
  return content
    .map((b) => (b.type === 'text' ? b.text : b.type === 'markdown' ? b.markdown : ''))
    .join('');
}
